import type { Source, Shape } from '@servicenow/sdk-build-core'
import {
    CallExpressionShape,
    PropertyAccessShape,
    StringLiteralShape,
    TemplateExpressionShape,
    TemplateSpanShape,
} from '@servicenow/sdk-build-core'
import { CURRENT_ACTIVITY_CALLEE, DATAPILL_CALLEE, TABLE_NAME_SUFFIX } from '../constants/playbook-plugin-constants'

// ============================================================================
// Types
// ============================================================================

/** Maps an activity sys_id to its TypeScript variable name and (optional) lane variable name. */
export interface ActivityIdentifier {
    varName: string
    laneVarName?: string
}

export interface PillTransformContext {
    /** Maps activity sys_id → variable name info for reconstructing pill references. */
    activityMap: Map<string, ActivityIdentifier>
    /** The sys_id of the activity whose fields are being transformed (for self-ref detection). */
    currentActivitySysId?: string
    /** Lane variable name of the current transform context (for same-lane detection). */
    currentLaneVarName?: string
}

/** A segment of a pill-containing string: either literal text or a resolved pill reference. */
type PillPart = { type: 'text'; text: string } | { type: 'pill'; ref: string; isTableName?: boolean }

// ============================================================================
// Constants
// ============================================================================

/**
 * Matches standard pill references: `{{<content>}}`
 * Capture group 1 is the content between the braces (e.g. `pd.inputs.varName`).
 */
export const STANDARD_PILL_REGEX = /\{\{([^}]+)\}\}/g

/**
 * Known ServiceNow encoded query operators, sorted longest-first so the regex
 * alternation matches the most specific operator at each position.
 *
 * Add new operators here as needed — the list is used to split condition
 * strings into tokens so pill prefixes can be matched safely.
 */
const CONDITION_OPERATORS = [
    // Multi-word operators
    'NOT LIKE',
    'NOT IN',
    // Word operators (longest first)
    'ISNOTEMPTY',
    'VALCHANGES',
    'EMPTYSTRING',
    'STARTSWITH',
    'ENDSWITH',
    'ISEMPTY',
    'ANYTHING',
    'BETWEEN',
    'DYNAMIC',
    'NSAMEAS',
    'SAMEAS',
    'LIKE',
    'IN',
    // Symbol operators (multi-char before single-char to prevent partial matches)
    '!=',
    '>=',
    '<=',
    '=',
    '>',
    '<',
]

/**
 * Splits a condition string into tokens (field names, values) and separators
 * (operators like `=` / `SAMEAS`, clause delimiters like `^` / `^OR`).
 *
 * The capturing group makes `String.split` keep separators in the result,
 * so we can inspect each token individually for pill prefixes without
 * substring false-positives (e.g. `user_inputs.priority` won't match `inputs.`).
 *
 * Longest alternatives come first so `^OR` is matched before `^`,
 * `NOT LIKE` before `LIKE`, `!=` before `=`, etc.
 */
const CONDITION_SPLIT_REGEX = new RegExp('(' + ['\\^OR', '\\^NQ', '\\^', ...CONDITION_OPERATORS].join('|') + ')')

// ============================================================================
// Standard pill parsing — {{...}} format
// ============================================================================

/**
 * Transform a string value from the platform that may contain standard pill
 * references (`{{pd.inputs.x}}`, `{{act.<id>.outputs.y}}`, etc.) into a Shape
 * suitable for Fluent code generation.
 *
 * - A value with no pills is returned unchanged (undefined).
 * - A value that is exactly one pill with no surrounding text becomes a bare
 *   `wfa.playbook.dataPill()` CallExpressionShape.
 * - A value mixing static text and pills becomes a TemplateExpressionShape.
 */
export function transformStandardPills(
    value: string,
    source: Source,
    context: PillTransformContext
): Shape | undefined {
    // Each match from matchAll is an array where:
    //   match[0] = the full matched string including braces, e.g. "{{pd.inputs.name}}"
    //   match[1] = capture group 1: the content between {{ and }}, e.g. "pd.inputs.name"
    //   match.index = position of the match in the input string
    const matches = [...value.matchAll(STANDARD_PILL_REGEX)]
    if (matches.length === 0) {
        return undefined
    }

    const parts = buildStandardPillParts(value, matches, context)
    if (!parts.some((p) => p.type === 'pill')) {
        return undefined
    }

    // When the entire value is a single pill with no surrounding text,
    // emit a bare wfa.playbook.dataPill() call rather than a template literal.
    const first = parts[0]
    if (parts.length === 1 && first?.type === 'pill') {
        return buildDataPillCall(source, first.ref, first.isTableName)
    }

    return buildTemplateFromParts(parts, source)
}

/**
 * Walk through regex matches and build an ordered list of text/pill parts.
 *
 * `textStart` tracks how far into the string we've consumed. For each resolved
 * pill, any characters between `textStart` and the pill's start position are
 * emitted as literal text. Unresolved pills are skipped — their `{{...}}` text
 * is left in place and will be swept into the next literal text chunk.
 */
function buildStandardPillParts(value: string, matches: RegExpExecArray[], context: PillTransformContext): PillPart[] {
    const parts: PillPart[] = []
    let textStart = 0

    for (const match of matches) {
        let pillContent = match[1] // content between {{ and }}
        const pillStart = match.index // where {{...}} begins in the string
        if (pillContent === undefined || pillStart === undefined) {
            continue
        }
        const pillEnd = pillStart + match[0].length // where {{...}} ends

        // Detect and strip .:TN suffix before resolution
        const isTableName = pillContent.endsWith(TABLE_NAME_SUFFIX)
        if (isTableName) {
            pillContent = pillContent.slice(0, -TABLE_NAME_SUFFIX.length)
        }

        const ref = resolveStandardPillContent(pillContent, context)
        if (!ref) {
            continue
        }

        // Any text between the end of the previous pill and the start of this one
        if (pillStart > textStart) {
            parts.push({ type: 'text', text: value.substring(textStart, pillStart) })
        }
        parts.push({ type: 'pill', ref, isTableName })
        textStart = pillEnd
    }

    // Any remaining text after the last resolved pill
    if (textStart < value.length) {
        parts.push({ type: 'text', text: value.substring(textStart) })
    }

    return parts
}

/**
 * Parse the content between `{{` and `}}` and return the Fluent property-access
 * string (e.g. `params.inputs.varName` or `activityVar.outputs.field`).
 */
function resolveStandardPillContent(content: string, context: PillTransformContext): string | undefined {
    if (content === 'pd.state') {
        return 'params.state'
    }
    if (content.startsWith('pd.inputs.')) {
        return `params.inputs.${content.substring('pd.inputs.'.length)}`
    }
    if (content === 'pd.inputRecord') {
        return 'params.parentRecord'
    }
    if (content.startsWith('pd.inputRecord.')) {
        return `params.parentRecord.${content.substring('pd.inputRecord.'.length)}`
    }

    // Trigger record pills: triggerRecord or triggerRecord.field
    if (content === 'triggerRecord') {
        return 'trigger.current'
    }
    if (content.startsWith('triggerRecord.')) {
        const path = content.substring('triggerRecord.'.length)
        return `trigger.current.${path}`
    }

    // Activity pill: act.<sysId>.<path>
    if (content.startsWith('act.')) {
        const rest = content.substring('act.'.length)
        const dotIndex = rest.indexOf('.')
        if (dotIndex === -1) {
            return undefined
        }
        const sysId = rest.substring(0, dotIndex)
        const path = rest.substring(dotIndex + 1)

        // Self-reference for label/description → use wfa.playbook.currentActivity
        if (sysId === context.currentActivitySysId && (path === 'label' || path === 'description')) {
            return `${CURRENT_ACTIVITY_CALLEE}.${path}`
        }

        // Map platform outputs.sys_id → Fluent sysId shorthand
        const effectivePath = path === 'outputs.sys_id' ? 'sysId' : path

        return resolveActivityRef(sysId, effectivePath, context)
    }

    // Automation plan pill
    if (content.startsWith('vl.')) {
        return content
    }

    return undefined
}

// ============================================================================
// Condition pill parsing — unwrapped format
// ============================================================================

/**
 * Transform a condition_to_run or branch condition string that may contain
 * unwrapped pill references into a Fluent template expression.
 *
 * Scans the entire string for pill prefixes in both field and value positions,
 * so conditions like `inputs.field1SAMEASinputs.field2` are fully resolved.
 *
 * Example input:  `activity_abc.outputs.status=completed^inputs.skipTriage=false`
 * Example output: `` `${wfa.playbook.dataPill(act1.outputs.status)}=completed^${wfa.playbook.dataPill(params.inputs.skipTriage)}=false` ``
 *
 * Returns undefined if the condition contains no pill references.
 */
export function transformConditionPills(
    value: string,
    source: Source,
    context: PillTransformContext
): Shape | undefined {
    const parts = scanForConditionPills(value, context)

    if (!parts.some((p) => p.type === 'pill')) {
        return undefined
    }

    return buildTemplateFromParts(parts, source)
}

/**
 * Scan a condition string for pill references (`activity_<sysId>.<path>`,
 * `inputs.<path>`, or `input_record.<path>`) and return an ordered list of
 * literal-text and pill parts.
 *
 * The string is first split on operators and clause delimiters so that each
 * token (field name or value) can be checked with `startsWith` — this avoids
 * false positives from substring matches like `user_inputs.` matching `inputs.`.
 *
 * Example: `activity_abc.outputs.statusSAMEASinputs.field^user_inputs.x=1`
 * splits into: ["activity_abc.outputs.status", "SAMEAS", "inputs.field", "^", "user_inputs.x", "=", "1"]
 * → pill, text, pill, text, text, text, text
 */
function scanForConditionPills(value: string, context: PillTransformContext): PillPart[] {
    const segments = value.split(CONDITION_SPLIT_REGEX)
    const parts: PillPart[] = []

    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i]
        if (!segment) {
            continue
        }

        // Odd indices are separators (operators / delimiters) — always literal text.
        // Even indices are tokens (field names / values) — check for pill prefixes.
        const isToken = i % 2 === 0
        const ref = isToken ? resolveConditionToken(segment, context) : undefined

        if (ref) {
            parts.push({ type: 'pill', ref })
        } else {
            // Merge adjacent text parts so downstream gets clean text/pill alternation
            const last = parts[parts.length - 1]
            if (last?.type === 'text') {
                last.text += segment
            } else {
                parts.push({ type: 'text', text: segment })
            }
        }
    }

    return parts
}

/**
 * Check whether a token (field name or value from a split condition string)
 * starts with a known pill prefix and resolve it to a Fluent property-access path.
 */
function resolveConditionToken(token: string, context: PillTransformContext): string | undefined {
    if (token.startsWith('inputs.')) {
        return `params.inputs.${token.substring('inputs.'.length)}`
    }
    if (token.startsWith('input_record.')) {
        return `params.parentRecord.${token.substring('input_record.'.length)}`
    }
    if (token.startsWith('activity_')) {
        const rest = token.substring('activity_'.length)
        const dotIndex = rest.indexOf('.')
        if (dotIndex === -1) {
            return undefined
        }
        const sysId = rest.substring(0, dotIndex)
        const path = rest.substring(dotIndex + 1)
        return resolveActivityRef(sysId, path, context)
    }
    return undefined
}

// ============================================================================
// Shared helpers
// ============================================================================

/**
 * Resolve an activity sys_id + dot-walk path to a Fluent property-access string.
 * Cross-lane references are prefixed with the lane variable name;
 * same-lane references use the activity variable name directly.
 */
function resolveActivityRef(sysId: string, path: string, context: PillTransformContext): string | undefined {
    const mapping = context.activityMap.get(sysId)
    if (!mapping) {
        return undefined
    }

    // Only prefix with lane name for cross-lane references
    if (mapping.laneVarName && mapping.laneVarName !== context.currentLaneVarName) {
        return `${mapping.laneVarName}.${mapping.varName}.${path}`
    }
    return `${mapping.varName}.${path}`
}

/**
 * Convert an ordered list of text/pill parts into a TemplateExpressionShape.
 *
 * Text parts before the first pill become the head literal. Each pill becomes
 * a span whose trailing literal starts empty; subsequent text parts are
 * appended to the most recent span's trailing literal.
 */
function buildTemplateFromParts(parts: PillPart[], source: Source): TemplateExpressionShape {
    let headText = ''
    const spans: TemplateSpanShape[] = []

    for (const part of parts) {
        if (part.type === 'text') {
            if (spans.length === 0) {
                headText += part.text
            } else {
                const prev = spans[spans.length - 1]
                if (prev) {
                    spans[spans.length - 1] = new TemplateSpanShape({
                        source,
                        expression: prev.getExpression(),
                        literalText: prev.getLiteralText() + part.text,
                    })
                }
            }
        } else {
            spans.push(
                new TemplateSpanShape({
                    source,
                    expression: buildDataPillCall(source, part.ref, part.isTableName),
                    literalText: '',
                })
            )
        }
    }

    return new TemplateExpressionShape({ source, literalText: headText, spans })
}

/**
 * Build a `wfa.playbook.dataPill(<ref>)` CallExpressionShape.
 * When `isTableName` is true, appends `_tableName` as a trailing property-access element.
 */
function buildDataPillCall(source: Source, ref: string, isTableName?: boolean): CallExpressionShape {
    if (ref.startsWith('vl.')) {
        return new CallExpressionShape({
            source,
            callee: DATAPILL_CALLEE,
            args: [new StringLiteralShape({ source, literalText: `{{${ref}}}` })],
        })
    }
    const base = ref.split('.')
    const elements = (isTableName ? [...base, '_tableName'] : base) as [string, string, ...string[]]
    return new CallExpressionShape({
        source,
        callee: DATAPILL_CALLEE,
        args: [new PropertyAccessShape({ source, elements })],
    })
}
